import { getAtPath } from "@/v2/framework/dialog/composables/useDialogContext";

import type {
  InheritablePatchResult,
  InheritanceFieldDescriptor,
  SourcePolicyValue,
} from "../types";
import { readEntitySlice } from "./entitySlice";

function normalizePatchValue(value: unknown): unknown {
  if (value === -1 || value === "" || value === undefined) return null;
  return value;
}

function valuesEqual(a: unknown, b: unknown): boolean {
  const left = normalizePatchValue(a);
  const right = normalizePatchValue(b);
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
  }
  if (
    (typeof left === "number" || typeof left === "string") &&
    (typeof right === "number" || typeof right === "string")
  ) {
    return String(left) === String(right);
  }
  return Object.is(left, right);
}

function setNestedPatch(patch: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let current = patch;

  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = current[key];
    if (!next || typeof next !== "object" || Array.isArray(next)) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  current[parts[parts.length - 1]!] = value;
}

function readBaselineFieldValue(
  baseline: Record<string, unknown>,
  descriptor: InheritanceFieldDescriptor,
): unknown {
  const entity = readEntitySlice(baseline, descriptor.entityPath);
  if (!entity) return undefined;
  return entity[descriptor.computedKey] ?? entity[descriptor.field];
}

function collectFieldPatches(options: {
  current: Record<string, unknown>;
  baseline: Record<string, unknown>;
  descriptors: InheritanceFieldDescriptor[];
}): Record<string, unknown> {
  const { current, baseline, descriptors } = options;
  const entityPatch: Record<string, unknown> = {};

  for (const descriptor of descriptors) {
    const currentValue = normalizePatchValue(getAtPath(current, descriptor.path));
    const baselineValue = normalizePatchValue(readBaselineFieldValue(baseline, descriptor));

    if (valuesEqual(currentValue, baselineValue)) continue;

    const nestedPath = descriptor.entityPath
      ? `${descriptor.entityPath}.${descriptor.patchKey}`
      : descriptor.patchKey;
    setNestedPatch(entityPatch, nestedPath, currentValue);
  }

  return entityPatch;
}

function collectSourcePolicyPatches(options: {
  currentPolicies: Record<string, SourcePolicyValue>;
  baselinePolicies: Record<string, SourcePolicyValue>;
  descriptors: InheritanceFieldDescriptor[];
}): Record<string, unknown> | undefined {
  const { currentPolicies, baselinePolicies, descriptors } = options;
  const rootChanges: Record<string, SourcePolicyValue> = {};
  const scopedChanges = new Map<string, Record<string, SourcePolicyValue>>();

  for (const descriptor of descriptors) {
    const current = currentPolicies[descriptor.path] ?? 1;
    const baseline = baselinePolicies[descriptor.path] ?? 1;
    if (current === baseline) continue;

    if (descriptor.sourcePolicyScope) {
      const bucket =
        scopedChanges.get(descriptor.sourcePolicyScope) ??
        (() => {
          const next: Record<string, SourcePolicyValue> = {};
          scopedChanges.set(descriptor.sourcePolicyScope, next);
          return next;
        })();
      bucket[descriptor.patchKey] = current;
      continue;
    }

    rootChanges[descriptor.patchKey] = current;
  }

  if (!Object.keys(rootChanges).length && scopedChanges.size === 0) return undefined;

  const patch: Record<string, unknown> = { ...rootChanges };
  for (const [scope, changes] of scopedChanges) {
    patch[scope] = changes;
  }

  return patch;
}

export function buildInheritablePatch(options: {
  current: Record<string, unknown>;
  baseline: Record<string, unknown>;
  descriptors: InheritanceFieldDescriptor[];
  currentPolicies: Record<string, SourcePolicyValue>;
  baselinePolicies: Record<string, SourcePolicyValue>;
}): InheritablePatchResult {
  const entityPatch = collectFieldPatches(options);
  const sourcePolicyPatch = collectSourcePolicyPatches(options);

  const patch: Record<string, unknown> = { ...entityPatch };
  if (sourcePolicyPatch) {
    patch.sourcePolicyMap = sourcePolicyPatch;
  }

  return {
    patch,
    hasChanges: Object.keys(entityPatch).length > 0 || !!sourcePolicyPatch,
  };
}

export function readBaselinePolicies(
  baseline: Record<string, unknown>,
  descriptors: InheritanceFieldDescriptor[],
): Record<string, SourcePolicyValue> {
  const policies: Record<string, SourcePolicyValue> = {};

  for (const descriptor of descriptors) {
    const entity = readEntitySlice(baseline, descriptor.entityPath);
    const byField = entity?.sourcePolicyByField as Record<string, number> | undefined;
    policies[descriptor.path] = (byField?.[descriptor.computedKey] as SourcePolicyValue) ?? 1;
  }

  return policies;
}
